import React, { Suspense } from 'react'
import { RenderBlock } from '@rst/render-blocks/src/types'
import { color, HTMLGlobalStyle } from '@rst/ui-ts'
import { createGlobalStyle } from 'styled-components'
import { lazyWithRetry, RequestStatusType } from '@rst/libs-ts'
import { useStore } from 'effector-react'
import { HeaderMain } from '@rst/render-blocks'
import { $nav, $info, $main, $head, $footer } from 'model'

import { $page } from '../../model'
import { Preloader } from './organisms/preloader'
import { RenderBlockThemeProvider } from './lib/render-block-theme'
import { LanguageProps } from '@rst/render-blocks'

const PageType = lazyWithRetry(
	() => import(/* webpackChunkName: "r.page" */ './organisms/page')
)
const Cart = lazyWithRetry(() => import('../../main-modules/cart'))
const CartPay = lazyWithRetry(() => import('../../main-modules/new-cart-pay'))
const Page404 = lazyWithRetry(() => import('./organisms/page404'))
const PageTour = lazyWithRetry(
	() => import('@rst/render-blocks/src/features/render-page/tour')
)

type PagePropsType = {
	data: RenderBlock.Page
	loading: RequestStatusType
	langProps: LanguageProps
}

const Render = React.memo(({ langProps }: { langProps: LanguageProps }) => {
	const { loading, data } = useStore($page)

	return (
		<RenderBlockThemeProvider>
			<Wrapper>
				<Suspense fallback={<Preloader />}>
					{pages[data?.type]
						? pages[data.type]({ data, loading, langProps })
						: null}
				</Suspense>
			</Wrapper>
		</RenderBlockThemeProvider>
	)
})

const Header = ({ langProps }: { langProps: LanguageProps }) => {
	const headerPos = useStore($head).head_pos
	return (
		<HeaderMain
			ctx={{
				$nav,
				$info,
				$main,
				$head,
				$footer,
			}}
			langProps={langProps}
			headerWrapper={Header => (
				<div
					style={{
						position: 'sticky',
						top: '0px',
						left: 0,
						zIndex: headerPos === 'bottom' ? 300 : 303,
					}}
				>
					{Header}
				</div>
			)}
			mainWrapper={Main => <div id="main-block-anim">{Main}</div>}
		/>
	)
}

const pages = {
	cart: ({ langProps }: PagePropsType) => (
		<>
			<Header langProps={langProps} />
			<Cart />
		</>
	),
	pay: (_: PagePropsType) => <CartPay />,
	'404': (_: PagePropsType) => <Page404 />,
	tour: ({ data, langProps }: PagePropsType & any) => {
		return (
			<>
				<Header langProps={langProps} />
				<PageTour {...data} disableBreadCrumbs />
			</>
		)
	},
	page: ({ data, loading, langProps }: PagePropsType) => (
		<PageType data={data as any} loading={loading} langProps={langProps} />
	),
}

const Wrapper: React.FC = ({ children }) => (
	<>
		<HTMLGlobalStyle />
		<GlobalStyle />
		{children}
	</>
)
const GlobalStyle = createGlobalStyle`
	body {
		background: ${color('gray-01')}
	}
`

export { Render }
